import {isFeatureEnabled} from 'feature-flags';
import {FlagProps, Parameter, Parameters} from 'platform/components';
import {
  ThemeIconKey,
  Box,
  Heading,
  Hide,
  HStack,
  Link,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';

import {HTMLAttributeAnchorTarget, isValidElement, ReactElement} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {ActionParameters} from '../../ActionParameters/ActionParameters';
import {DynamicFlagList} from './DynamicFlagList';
import {SkeletonLoadingBox} from './SkeletonLoadingBox';
import {SnippetPlaceholder, SnippetPlaceholderProps} from './SnippetPlaceholder';

export interface SnippetProps extends TestIdProps {
  label: string;
  title?: string | ReactElement;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  flags?: FlagProps[] | null;
  icon?: ThemeIconKey;
  parameters?: Parameter[];
  placeholder?: SnippetPlaceholderProps;
  isDisabled?: boolean;
  onClick?: () => void;
  customerId?: string;
  selectedContactId?: string;
  resourceId?: EntityResourceIds;
  recordId?: string;
  vehicleId?: string | Nullish;
  isLoading?: boolean;
}

const isTitleString = (title: SnippetProps['title']): title is string => typeof title === 'string';

const isTitleReactElement = (title: SnippetProps['title']): title is ReactElement =>
  isValidElement(title);

export function Snippet(props: SnippetProps) {
  const hasContent = !!(props.title || isNotNilOrEmpty(props.flags));
  const shouldShowActionParameters =
    props.label === i18n.t('entity.businessCase.labels.contactPerson') ||
    props.label === i18n.t('entity.customer.labels.customer');

  const isForVehicleHeaderV3 =
    props.resourceId === 'VEHICLE' && isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3);

  return (
    <Box data-testid={props['data-testid']}>
      <Text
        size="xSmall"
        color={isForVehicleHeaderV3 ? 'secondary' : undefined}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Text>

      <Space vertical={1} />

      <Box
        borderRadius="small"
        backgroundColor="palettes.neutral.10.100"
        opacity={props.isDisabled ? 0.4 : 1}
        height={14}
        position="relative"
      >
        <SkeletonLoadingBox isLoading={props.isLoading} height={14} borderRadius="small">
          <Show when={hasContent}>
            <Box width="100%" height="100%" padding={2}>
              <VStack
                width="100%"
                height="100%"
                justify={isNil(props.title) ? 'flex-start' : 'center'}
                spacing={1}
              >
                <HStack spacing={2} align="center">
                  <Show when={props.title}>
                    <Show when={isTitleReactElement(props.title)}>{props.title}</Show>

                    <Show when={isTitleString(props.title)}>
                      <Show when={props.onClick ?? props.href}>
                        <Box flexShrink={0}>
                          <Link
                            size="small"
                            href={props.href}
                            title={props.title as string}
                            onClick={props.onClick}
                            isDisabled={props.isDisabled}
                            leftIcon={props.icon}
                            data-testid={suffixTestId(`link`, props)}
                            target={props.target}
                          />
                        </Box>
                      </Show>
                      <Show when={!props.onClick && !props.href}>
                        <Heading
                          size={5}
                          headingLevel={6}
                          data-testid={suffixTestId(`heading`, props)}
                        >
                          {props.title as string}
                        </Heading>
                      </Show>
                    </Show>
                  </Show>

                  <Show when={isNotNilOrEmpty(props.flags)}>
                    <DynamicFlagList data={props.flags ?? []} />
                  </Show>
                </HStack>
                <Show when={shouldShowActionParameters}>
                  <ActionParameters
                    parameters={props.parameters}
                    customerId={props.customerId}
                    color="secondary"
                    tooltipLabelSms={i18n.t('general.actions.sendMessage')}
                    tooltipLabelEmail={i18n.t('general.actions.sendEmail')}
                    resourceId={props.resourceId}
                    recordId={props.recordId}
                    vehicleId={props.vehicleId}
                  />
                </Show>
                <Hide when={shouldShowActionParameters}>
                  <Parameters
                    color="secondary"
                    size="xSmall"
                    parameters={props.parameters}
                    data-testid={suffixTestId(`parameters`, props)}
                  />
                </Hide>
              </VStack>
            </Box>
          </Show>

          <Hide when={hasContent}>
            <SnippetPlaceholder
              isDisabled={props.isDisabled}
              {...props.placeholder}
              data-testid={suffixTestId(`placeholder`, props)}
            />
          </Hide>
        </SkeletonLoadingBox>
      </Box>
    </Box>
  );
}
